import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { message, Modal, Input } from 'antd';

import { PlusOutlined, SearchOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import Button from '../../../shared/globalStyling/Button';
import { useBreakpoints } from '../../../shared/globalStyling/screenSizes';
import CreateRegionGroup from './CreateRegionGroup';
import RegionGroupListView from './RegionGroupListView';

const RegionGroup = ({
  toolbarContainer,
  regionGroups,
  handleSubmitRegionGroup,
  handleUpdateRegionGroup,
  deleteRegionGroup,
  searchRegionLiquidM,
  searchRegionLiquidMData,
  searchRegionLiquidMLoading,
  presetRegionGroups,
  presetRegionGroupsLoading,
  google,
  selectedOrg,
  currentuser,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentRegion, setCurrentRegion] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { isTablet } = useBreakpoints();

  const toggleForm = (val) => {
    setIsFormOpen(val);
  };

  const onDeleteRegionGroup = (Id) => {
    toggleForm(false);

    message.loading('Action in progress..', 0);

    deleteRegionGroup(Id)
      .then(() => {
        message.destroy();
        message.success(`Successfully Deleted Region Group`);
      })
      .catch(() => {
        message.destroy();
        message.error(`Error: Cannot Delete Region Group`);
      });
  };

  const handleBulkDelete = async () => {
    message.loading('Action in progress..', 0);

    try {
      await Promise.all(selectedRows.map((row) => deleteRegionGroup(row.id)));
      message.destroy();
      message.success(`Successfully deleted ${selectedRows.length} region group(s)`);
      setSelectedRows([]);
      setIsDeleteOpen(false);
    } catch {
      message.destroy();
      message.error('Failed to delete region group(s)');
      setIsDeleteOpen(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
  };

  const handleCreateRegion = () => {
    setCurrentRegion(null);
    toggleForm(true);
  };

  const hasSelection = selectedRows.length > 0;

  // ─── Toolbar: search + create button (portaled into tab bar) ──────
  const toolbarButtons = (
    <>
      <div className="targeting-search">
        <Input
          placeholder="Search region groups"
          value={searchText}
          allowClear
          prefix={<SearchOutlined />}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>
      {!isTablet && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreateRegion()}>
          Create Region Group
        </Button>
      )}
    </>
  );

  return (
    <div className="targeting-region">
      {/* Portal search + actions into the tab bar */}
      {toolbarContainer && ReactDOM.createPortal(toolbarButtons, toolbarContainer)}

      {isFormOpen && (
        <CreateRegionGroup
          isFormOpen={isFormOpen}
          handleSubmitRegionGroup={handleSubmitRegionGroup}
          handleUpdateRegionGroup={handleUpdateRegionGroup}
          searchRegionLiquidM={searchRegionLiquidM}
          searchRegionLiquidMData={searchRegionLiquidMData}
          searchRegionLiquidMLoading={searchRegionLiquidMLoading}
          currentRegion={currentRegion}
          closeForm={toggleForm}
          presetRegionGroups={presetRegionGroups}
          presetRegionGroupsLoading={presetRegionGroupsLoading}
          google={google}
        />
      )}

      {/* Delete confirmation modal */}
      {isDeleteOpen && hasSelection && (
        <Modal
          title="Confirm Action"
          visible={isDeleteOpen}
          onOk={handleBulkDelete}
          onCancel={() => setIsDeleteOpen(false)}
          wrapClassName="targeting-root"
        >
          <p>Are you sure you want to delete ({selectedRows.length}) region group(s)?</p>
        </Modal>
      )}

      {/* ─── Selection alert bar (matches Audiences tab) ────────── */}
      {hasSelection && (
        <div className="targeting-selection-alert" role="status" aria-live="polite">
          <div className="targeting-selection-alert__info">
            <span className="targeting-selection-alert__count">{selectedRows.length}</span>
            <span className="targeting-selection-alert__text">
              region group{selectedRows.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="targeting-selection-alert__actions">
            <button
              type="button"
              className="targeting-selection-alert__link targeting-selection-alert__link--danger"
              onClick={() => setIsDeleteOpen(true)}
            >
              <DeleteOutlined className="targeting-selection-alert__link-icon" />
              Delete
            </button>
            <button
              type="button"
              className="targeting-selection-alert__close"
              onClick={handleClearSelection}
              aria-label="Clear selection"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="targeting-region__content">
        <RegionGroupListView
          handleSubmitRegionGroup={handleSubmitRegionGroup}
          regionGroups={regionGroups}
          currentuser={currentuser}
          selectedOrg={selectedOrg}
          setCurrentRegion={setCurrentRegion}
          toggleForm={toggleForm}
          searchText={searchText}
          setSelectedRows={setSelectedRows}
        />
      </div>

      {/* Mobile FAB */}
      {isTablet && (
        <button
          type="button"
          className="targeting-fab"
          onClick={handleCreateRegion}
          aria-label="Create Region Group"
        >
          <PlusOutlined />
        </button>
      )}
    </div>
  );
};

export default RegionGroup;
